// "New text": a number in any format or a contact search, then open (or create) the thread.
// Phase 2: an optional first-message template. It is rendered with the company and the
// contact's name and handed to the composer as a prefill (editable, still the normal send path).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatPhone, renderTemplate, unresolvedTokens } from '@bt/core';
import { api, type MeResponse } from '../../api';
import { Dialog } from '../../components/Dialog';
import { ContactPicker, type Picked } from '../../components/ContactPicker';
import { InlineError } from '../../components/States';
import { CATEGORY_LABEL, categoryOf, pickWording, TokenForm } from '../../components/TemplatePicker';

export function NewTextDialog({ me, onClose }: { me: MeResponse; onClose: () => void }) {
  const navigate = useNavigate();
  const activeLines = me.lines.filter((l) => l.active !== false);
  const [lineId, setLineId] = useState(activeLines[0]?.id ?? '');
  const [templateId, setTemplateId] = useState('');
  const [wording, setWording] = useState('');
  const [extra, setExtra] = useState<Record<string, string>>({});

  const templates = useQuery({ queryKey: ['templates'], queryFn: api.templates.list, staleTime: 30_000 });
  const template = templates.data?.items.find((t) => t.id === templateId) ?? null;
  // first-message templates first; reminders need a date so they sit last
  const ordered = useMemo(() => {
    const items = templates.data?.items ?? [];
    const rank = { first_message: 0, other: 1, reminder: 2 } as const;
    return [...items].sort((a, b) => rank[categoryOf(a)] - rank[categoryOf(b)] || a.name.localeCompare(b.name));
  }, [templates.data]);

  const company = me.workspace.company_name ?? me.workspace.name;
  // the name is unknown until a contact is picked; templates use {{name|there}} so it is fine either way
  const knownVars: Record<string, string> = { company };
  const missing = wording ? unresolvedTokens(wording, { ...knownVars, name: 'x' }) : [];
  const stillMissing = wording ? unresolvedTokens(wording, { ...knownVars, name: 'x', ...extra }) : [];

  const create = useMutation({
    mutationFn: (p: Picked) =>
      api.conversations.create({
        contact_id: p.contact?.id,
        phone_number: p.contact ? undefined : p.phone_number,
        line_id: lineId || undefined,
      }),
    onSuccess: (res, p) => {
      const prefill = wording ? renderTemplate(wording, { ...knownVars, ...extra, name: p.contact?.name?.trim() ?? '' }) : null;
      onClose();
      navigate(`/inbox/${res.conversation.id}`, { state: prefill ? { prefill } : undefined });
    },
  });

  return (
    <Dialog title="New text" onClose={onClose}>
      {activeLines.length === 0 && <div className="notice warn">No texting line is set up yet. An admin can add one in Settings › Lines.</div>}
      {activeLines.length > 1 && (
        <label className="field">
          <span>Send from</span>
          <select className="input" value={lineId} onChange={(e) => setLineId(e.target.value)}>
            {activeLines.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label ? `${l.label} · ` : ''}
                {formatPhone(l.phone_number)}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span>Start with a template (optional)</span>
        <select
          className="input"
          value={templateId}
          onChange={(e) => {
            const id = e.target.value;
            setTemplateId(id);
            const t = ordered.find((x) => x.id === id) ?? null;
            setWording(t ? pickWording(t) : '');
            setExtra({});
          }}
        >
          <option value="">Blank</option>
          {ordered.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} · {CATEGORY_LABEL[categoryOf(t)]}
            </option>
          ))}
        </select>
      </label>
      {template && missing.length > 0 && <TokenForm tokens={missing} values={extra} onChange={(k, v) => setExtra((x) => ({ ...x, [k]: v }))} />}
      {template && (
        <div className="preview-box small" style={{ marginBottom: 12 }}>
          {renderTemplate(wording, { ...knownVars, ...extra })}
          <div className="tiny muted" style={{ marginTop: 4 }}>You can still edit it before sending.</div>
        </div>
      )}
      <label className="field">
        <span>To</span>
        <ContactPicker autoFocus={!template} onPick={(p) => create.mutate(p)} />
        <div className="field-help">Type a number in any format, or search by name. Enter picks the first match.</div>
      </label>
      {template && stillMissing.length > 0 && <div className="tiny warn-text" style={{ marginBottom: 8 }}>Fill in {stillMissing.join(', ')} above, or the text will have a gap.</div>}
      {create.isPending && <div className="muted small">Opening</div>}
      <InlineError error={create.error} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}
